import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Api_Feed, Api_Playlist_Collection, Api_Video } from '@core/api'
import { Api_Video_Type } from '@core/api'
import { useApiWithParams } from '@core/api/hooks/useApi'
import { customEvent, EventType } from '@core/customEvent'
import { createFeedWithInitVideoDetect, isVideoFeed } from '@core/feed/utils'
import { filterTruthy } from '@core/helpers/filterTruthy'
import { useGlobalStore } from '@core/hooks/useGlobalStore'
import { getHydratedVideosSelector } from '@core/hydration'
import { logInfo } from '@core/logging'
import {
  KEY_FIRST_VIDEOS_RECEIVED,
  KEY_VIDEOS_STARTED,
  usePerformance,
} from '@core/performance'
import type { IAnyWidgetParams } from '@core/types'

import type { FeedQuery, FeedQueryState, FeedSourceConfig } from './types'
import { FeedType } from './types'

const DEFAULT_PAGE = 10

export interface Props {
  config: FeedSourceConfig | FeedSourceConfig[]
  params: IAnyWidgetParams
  feed?: Api_Feed | Api_Playlist_Collection | null
}

const updateFeedItemsWithHydratedPartials = (
  videos: Api_Video[],
  hydratedVideos?: Record<string, Partial<Api_Video>>,
): Api_Video[] => {
  return videos.map((video) => {
    if (!hydratedVideos?.[video.encoded_id]) {
      return video
    } else {
      return {
        ...video,
        ...hydratedVideos[video.encoded_id],
      }
    }
  })
}

/**
 * Hook that returns a feed and a function to load more videos. It sends a
 * request to the API to get the feed if no hydrated feed is provided.
 */
export const useFeedQuery = ({
  config,
  params,
  feed: hydratedFeed,
}: Props): FeedQuery => {
  const configs = useMemo(
    () => (Array.isArray(config) ? config : [config]),
    [config],
  )
  const api = useApiWithParams(params)
  const performance = usePerformance(params)
  const hydratedVideos = useGlobalStore(getHydratedVideosSelector)
  const maxVideos = configs[0].maxVideos
  const pageSize = useMemo(
    () => Math.min(DEFAULT_PAGE, maxVideos || Infinity),
    [maxVideos],
  )

  const [{ loading, feed, error, config: winningConfig }, setState] =
    useState<FeedQueryState>({
      loading: !hydratedFeed,
      feed: hydratedFeed || null,
      error: null,
      config: hydratedFeed ? configs[0] : null,
    })

  const sanitizedVideoTypes = useMemo<Api_Video_Type[]>(() => {
    if (!params.video_types || params.video_types === '') {
      return []
    }
    const validTypes: Api_Video_Type[] = []
    const enumValues = Object.values(Api_Video_Type)

    params.video_types.split(',').forEach((type) => {
      const trimmedType = type.trim() as Api_Video_Type

      if (
        enumValues.includes(trimmedType) &&
        !validTypes.includes(trimmedType)
      ) {
        validTypes.push(trimmedType)
      } else {
        logInfo(`Ignored invalid video_type: ${type}`)
      }
    })

    return validTypes
  }, [params.video_types])

  const createFeedRequests = useMemo(() => {
    const requests = configs.map((config) => {
      switch (config.type) {
        case FeedType.USER_TIMELINE:
          return (pageSize) =>
            createFeedWithInitVideoDetect({
              config,
              createFeedApi: api.createEmbedUserTimelineFeed,
              params: {
                channel: config.username ?? '',
                pageSize,
                firstVideoId: config.firstVideoId,
              },
            })

        case FeedType.USER_TIMELINE_HASHTAG:
          return (pageSize) =>
            createFeedWithInitVideoDetect({
              config,
              createFeedApi: api.createEmbedUserTimelineHashtagFeed,
              params: {
                channel: config.username ?? '',
                pageSize,
                hashtagExpression: config.hashtagExpression ?? '',
                firstVideoId: config.firstVideoId,
              },
            })
        case FeedType.PRODUCT_SKU:
          return (pageSize) =>
            api.createEmbedUserProductSKUFeed({
              channel: config.username ?? '',
              pageSize,
              skus: config.skus ?? '',
              videoTypes: sanitizedVideoTypes,
            })
        case FeedType.PLAYLIST:
          return (pageSize) =>
            createFeedWithInitVideoDetect({
              config,
              createFeedApi: api.createEmbedPlaylistFeed,
              params: {
                channel: config.username ?? '',
                playlistId: config.playlistId ?? '',
                pageSize,
                firstVideoId: config.firstVideoId,
              },
            })
        case FeedType.PLAYLIST_COLLECTION:
          return () =>
            api.createEmbedPlaylistCollectionFeed({
              id: params.playlist_collection,
              channel: params.channel,
            })
        case FeedType.USER_VIDEOS:
          return (pageSize) =>
            api.createEmbedUserVideosFeed({
              channel: config.username ?? '',
              pageSize,
              videoIds: config.videoIds ?? [],
            })
        default:
          // For completeness, but this case shouldn't be hit
          return null
      }
    })

    return filterTruthy(requests)
  }, [
    configs,
    api,
    params.playlist_collection,
    params.channel,
    sanitizedVideoTypes,
  ])

  const createFeed = useCallback(async () => {
    try {
      if (createFeedRequests.length) {
        const promises = createFeedRequests.map((request) => request(pageSize))
        for (let i = 0; i < promises.length; i++) {
          try {
            const feed = await promises[i]
            if (isVideoFeed(feed)) {
              if (feed.feed_items.length) {
                const updatedFeed = {
                  ...feed,
                  feed_items: feed.feed_items.map((v) => {
                    const { variant, video } = { ...v }
                    if (params.force_replay) {
                      video.live_stream_replay_enabled = true
                    }
                    return { variant, video }
                  }),
                }
                setState((prevState) => ({
                  ...prevState,
                  loading: false,
                  feed: updatedFeed,
                  config: configs[i],
                }))
                return updatedFeed
              }
            } else if (feed.playlists.length) {
              setState((prevState) => ({
                ...prevState,
                loading: false,
                feed,
                config: configs[i],
              }))
              return feed
            }
          } catch (e) {}
        }
        throw new Error('No configs returned a valid feed')
      }
    } catch (e) {
      params.target &&
        customEvent({
          data: { detail: 'error', name: params.name },
          target: params.target,
          type: [EventType.FW_ERROR, EventType.FW_EMBEDFEED_ERROR],
        })
      setState({
        loading: false,
        feed: null,
        error: e,
        config: null,
      })
    }
  }, [
    configs,
    createFeedRequests,
    pageSize,
    params.force_replay,
    params.target,
    params.name,
  ])

  const feedPlaylistId = feed?.playlist_id
  // We want to pull out a playlist ID from our config, or from the array of
  // configs (in the case of a playlist collection, etc.). We only need to do
  // that if we have loaded a feed that has a playlist id.
  // We do?.['playlistId'] to get around the intersection types
  const configPlaylistId =
    config?.['playlistId'] ||
    (Array.isArray(config) && feed?.playlist_id
      ? config.find((c) => c['playlistId'])?.['playlistId']
      : null)
  const playlistIdChanged = useMemo(() => {
    return !(
      (!configPlaylistId && !feedPlaylistId) ||
      configPlaylistId === feedPlaylistId
    )
  }, [configPlaylistId, feedPlaylistId])

  /**
   * Trigger feed creation (API request).
   * Note: Make sure the custom event only fires one time.
   */
  const contentReadyDispatched = useRef(false)
  useEffect(() => {
    // feed should be sufficient for most cases but if we are using a playlist
    // collection, we need to check if the playlist id matches
    if (feed && !playlistIdChanged) {
      return
    }
    const handleSetup = async () => {
      performance?.mark(KEY_VIDEOS_STARTED)

      const feed = await createFeed()
      if (!isVideoFeed(feed)) {
        return
      }

      if (!contentReadyDispatched.current && params.target) {
        customEvent({
          data: {
            feed: feed.feed_items,
            name: params.name,
          },
          type: EventType.FW_EMBEDFEED_CONTENTREADY,
          target: params.target,
        })

        contentReadyDispatched.current = true
      }
      performance?.mark(KEY_FIRST_VIDEOS_RECEIVED)
      performance?.measure(KEY_FIRST_VIDEOS_RECEIVED, KEY_VIDEOS_STARTED)
    }

    handleSetup()
  }, [
    params.target,
    params.name,
    feed,
    createFeed,
    performance,
    playlistIdChanged,
  ])

  const hasMore = useMemo(() => {
    if (feed && isVideoFeed(feed)) {
      // [ZE-349]: Feed items should be defaulted to [], however given the sentry logs it seems that in rare cases
      // feed_items is undefined. As a result I added ?.length check. Once we verify this was the root cause we can
      // dig deeper on why this values type is not as expected: Followup task [GROW-1177]
      if (maxVideos && maxVideos <= (feed.feed_items?.length ?? 0)) {
        return false
      } else if (feed.next_page) {
        return true
      }
    }
    return false
  }, [feed, maxVideos])

  const loadMore = useCallback(
    async (countOfNextVideos) => {
      if (countOfNextVideos > 3 || !hasMore || !isVideoFeed(feed)) {
        return
      }
      try {
        if (
          !feed?.next_page ||
          loading ||
          (maxVideos && feed?.feed_items && feed.feed_items.length >= maxVideos)
        ) {
          return
        }
        const pageSize = Math.min(
          DEFAULT_PAGE,
          maxVideos
            ? maxVideos - (feed?.feed_items ? feed?.feed_items.length : 0)
            : Infinity,
        )

        const feedWithNewItems = feed
          ? await api.getMoreFeedItems(feed, pageSize)
          : null

        setState((state) => ({
          ...state,
          feed: {
            ...feed,
            ...feedWithNewItems,
            feed_items: [
              ...(feed?.feed_items ?? []),
              ...(feedWithNewItems?.feed_items ?? []),
            ],
          } as Api_Feed,
        }))
      } catch (e) {
        if (e?.status === 404) {
          await createFeed()
        } else {
          setState((state) => ({
            ...state,
            loading: false,
            feed,
            error: e,
          }))
        }
      }
    },
    [hasMore, feed, loading, maxVideos, api, createFeed],
  )

  const videos = useMemo(() => {
    if (!isVideoFeed(feed)) {
      return []
    }
    const videosToHydrate = (feed?.feed_items || [])
      .filter((item) => !!item.video)
      .map((item) => {
        item.video.variant = item.variant
        return item.video
      })
    return (
      updateFeedItemsWithHydratedPartials(videosToHydrate, hydratedVideos) ?? []
    )
  }, [feed, hydratedVideos])

  const playlists = useMemo(() => {
    if ((feed as Api_Playlist_Collection)?.playlists) {
      return (feed as Api_Playlist_Collection).playlists
    }

    return []
  }, [feed])

  return [
    { error, videos, loading, playlists, feed, config: winningConfig },
    { loadMore },
  ]
}
